<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row align-v="center" class="mt-3">
        <b-col>
          <h1>Onboarding Uber Eats</h1>
          <p class="text-muted mb-0">
            Conecta una cuenta de comercio de Uber Eats y activa nuevas tiendas con el flujo
            de "Integration Activation".
          </p>
        </b-col>
        <b-col cols="auto">
          <base-button type="secondary" size="sm" @click="refreshAll" :disabled="loading">
            <b-icon-arrow-clockwise></b-icon-arrow-clockwise> Refrescar
          </base-button>
        </b-col>
      </b-row>

      <!-- Step 1: OAuth connection -->
      <b-card class="mt-3">
        <h4>1. Conexión OAuth con Uber</h4>
        <b-row align-v="center">
          <b-col>
            <p class="mb-1">
              <strong>Estado:</strong>
              <b-badge :variant="status.connected ? 'success' : 'warning'">
                {{ status.connected ? 'Conectado' : 'No conectado' }}
              </b-badge>
              <span v-if="status.connected && status.token_expires_at" class="ml-2 text-muted">
                Expira: {{ status.token_expires_at }}
              </span>
            </p>
            <p class="mb-1 text-muted small">
              <strong>redirect_uri:</strong>
              <code>{{ status.oauth_redirect_uri || '(no configurado, set UBER_OAUTH_REDIRECT_URI o company.uber_oauth_redirect_uri)' }}</code>
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button
              v-if="!status.connected"
              type="success"
              size="sm"
              :disabled="loading || !status.oauth_credentials_set"
              @click="startOAuth"
            >Conectar con Uber</base-button>
            <base-button
              v-else
              type="warning"
              size="sm"
              :disabled="loading"
              @click="startOAuth"
            >Reconectar</base-button>
            <base-button
              v-if="status.connected"
              type="danger"
              size="sm"
              class="ml-2"
              :disabled="loading"
              @click="confirmDisconnect"
            >Desconectar</base-button>
          </b-col>
        </b-row>
        <b-alert :show="!status.oauth_credentials_set" variant="danger" class="mt-3 mb-0">
          Esta compañía no tiene <code>uber_oauth_client_id</code>/<code>uber_oauth_client_secret</code>
          configurados (ni en la tabla company ni vía env <code>UBER_OAUTH_CLIENT_ID</code>/<code>UBER_OAUTH_CLIENT_SECRET</code>).
          Estas credenciales corresponden a la app OAuth registrada en Uber Developer Portal con scope <code>eats.pos_provisioning</code>.
        </b-alert>
      </b-card>

      <!-- Step 2: stores -->
      <b-card class="mt-3" v-if="status.connected">
        <b-row align-v="center">
          <b-col>
            <h4 class="mb-0">2. Tiendas disponibles en Uber</h4>
            <p class="text-muted small mb-0">
              Selecciona las tiendas a activar y mapea cada una a una sucursal local.
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button type="primary" size="sm" :disabled="loading" @click="fetchStores">
              Cargar tiendas
            </base-button>
          </b-col>
        </b-row>

        <b-table
          v-if="stores.length > 0"
          :items="stores"
          :fields="storeFields"
          striped
          small
          responsive="md"
          class="mt-3"
        >
          <template #cell(select)="data">
            <b-form-checkbox v-model="data.item._selected"></b-form-checkbox>
          </template>
          <template #cell(location)="data">
            <span v-if="data.item.location && data.item.location.address" class="small">
              {{ data.item.location.address }}
            </span>
            <span v-else class="text-muted small">—</span>
          </template>
          <template #cell(sucursal)="data">
            <b-form-select v-model="data.item._sucursal_id" size="sm">
              <template #first>
                <b-form-select-option :value="null">-- no enlazar --</b-form-select-option>
              </template>
              <b-form-select-option v-for="s in sucursales" :key="s.id" :value="s.id">
                {{ s.nombre }} ({{ s.code }})
              </b-form-select-option>
            </b-form-select>
          </template>
          <template #cell(partner_store_id)="data">
            <b-form-input
              v-model="data.item._partner_store_id"
              size="sm"
              placeholder="default: sucursal.id"
            ></b-form-input>
          </template>
        </b-table>

        <b-alert v-if="loadedStores && stores.length === 0" show variant="info" class="mt-3">
          Uber no devolvió tiendas elegibles para este comercio.
        </b-alert>

        <div v-if="stores.length > 0" class="mt-3">
          <base-button
            type="success"
            size="sm"
            :disabled="loading || selectedCount === 0"
            @click="provisionSelected"
          >
            Activar {{ selectedCount }} tienda(s)
          </base-button>
          <base-button
            type="danger"
            size="sm"
            class="ml-2"
            :disabled="loading || selectedCount === 0"
            @click="deprovisionSelected"
          >
            Desactivar las marcadas
          </base-button>
        </div>
      </b-card>

      <!-- Step 3: events -->
      <b-card class="mt-3">
        <b-row align-v="center">
          <b-col>
            <h4 class="mb-0">3. Historial de eventos</h4>
            <p class="text-muted small mb-0">
              Requests enviados desde el dashboard + confirmaciones vía webhook
              (<code>store.provisioned</code>, <code>store.deprovisioned</code>).
            </p>
          </b-col>
          <b-col cols="auto">
            <base-button type="secondary" size="sm" :disabled="loading" @click="fetchEvents">
              Recargar
            </base-button>
          </b-col>
        </b-row>
        <b-table
          v-if="events.length > 0"
          :items="events"
          :fields="eventFields"
          striped
          small
          responsive="md"
          class="mt-3"
        ></b-table>
        <p v-else class="text-muted small mb-0 mt-3">Sin eventos todavía.</p>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";

export default {
  name: "UberOnboarding",
  components: { Nav },
  data() {
    return {
      loading: false,
      status: {
        connected: false,
        token_expires_at: "",
        oauth_redirect_uri: "",
        oauth_client_id: "",
        oauth_credentials_set: false,
      },
      sucursales: [],
      stores: [],
      loadedStores: false,
      events: [],
      storeFields: [
        { key: "select", label: "" },
        { key: "name", label: "Nombre" },
        { key: "store_id", label: "store_id" },
        { key: "external_store_id", label: "external_store_id" },
        { key: "location", label: "Dirección" },
        { key: "sucursal", label: "Sucursal local" },
        { key: "partner_store_id", label: "partner_store_id" },
      ],
      eventFields: [
        { key: "created_at", label: "Cuándo" },
        { key: "operation", label: "Operación" },
        { key: "event_type", label: "Webhook" },
        { key: "store_id", label: "store_id" },
        { key: "partner_store_id", label: "partner_store_id" },
        { key: "status", label: "Estado" },
        { key: "http_code", label: "HTTP" },
        { key: "message", label: "Mensaje" },
      ],
    };
  },
  created() {
    this.handleOAuthReturn();
    this.refreshAll();
  },
  computed: {
    selectedCount() {
      return this.stores.filter((s) => s._selected).length;
    },
  },
  methods: {
    handleOAuthReturn() {
      const q = this.$route.query;
      if (q.uber_oauth_success) {
        this.makeToast("Conectado", "Uber autorizó la conexión", "success");
        this.$router.replace({ query: {} });
      } else if (q.uber_oauth_error) {
        this.makeToast("Error OAuth", q.uber_oauth_error, "danger");
        this.$router.replace({ query: {} });
      }
    },
    refreshAll() {
      this.loadStatus();
      this.loadSucursales();
      this.fetchEvents();
    },
    loadStatus() {
      this.loading = true;
      axios
        .get("/uber/onboarding/status")
        .then((r) => {
          this.status = r.data;
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    loadSucursales() {
      axios
        .get("/sucursals")
        .then((r) => {
          this.sucursales = r.data.sort((a, b) => a.nombre.localeCompare(b.nombre));
        })
        .catch((err) => this.handleErr(err));
    },
    startOAuth() {
      const returnURL =
        window.location.origin + this.$router.resolve({ name: "UberOnboarding" }).href;
      this.loading = true;
      axios
        .get("/uber/onboarding/oauth/start", { params: { return_url: returnURL } })
        .then((r) => {
          if (r.data && r.data.authorize_url) {
            window.location.href = r.data.authorize_url;
          } else {
            this.makeToast("Error", "El backend no devolvió authorize_url", "danger");
          }
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    confirmDisconnect() {
      this.$bvModal
        .msgBoxConfirm(
          "Esto borra el merchant user token. Tendrás que volver a autorizar para usar provisioning. ¿Continuar?",
          { title: "Desconectar Uber", okVariant: "danger", okTitle: "Desconectar", cancelTitle: "Cancelar", centered: true }
        )
        .then((ok) => {
          if (ok) this.disconnect();
        });
    },
    disconnect() {
      this.loading = true;
      axios
        .post("/uber/onboarding/oauth/disconnect")
        .then(() => {
          this.makeToast("Desconectado", "Token de comercio eliminado", "success");
          this.loadStatus();
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    fetchStores() {
      this.loading = true;
      axios
        .get("/uber/onboarding/stores")
        .then((r) => {
          const list = (r.data && r.data.stores) || r.data || [];
          this.stores = list.map((s) => ({
            name: s.name || "",
            store_id: s.store_id || "",
            external_store_id: s.external_store_id || "",
            location: s.location || {},
            _selected: false,
            _sucursal_id: null,
            _partner_store_id: "",
          }));
          this.loadedStores = true;
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    provisionSelected() {
      const stores = this.stores
        .filter((s) => s._selected)
        .map((s) => ({
          store_id: s.store_id,
          partner_store_id: s._partner_store_id || "",
          store_configuration_data: "",
          sucursal_id: s._sucursal_id || 0,
        }));
      if (stores.length === 0) return;
      this.loading = true;
      axios
        .post("/uber/onboarding/provisioning", { stores })
        .then((r) => {
          const results = (r.data && r.data.results) || [];
          const ok = results.filter((x) => x.status === "REQUESTED").length;
          const fail = results.length - ok;
          this.makeToast(
            "Resultado",
            `OK: ${ok}, Errores: ${fail}. Uber confirmará por webhook.`,
            fail === 0 ? "success" : "warning"
          );
          this.fetchEvents();
        })
        .catch((err) => this.handleErr(err))
        .finally(() => (this.loading = false));
    },
    deprovisionSelected() {
      const stores = this.stores
        .filter((s) => s._selected)
        .map((s) => ({ store_id: s.store_id }));
      if (stores.length === 0) return;
      this.$bvModal
        .msgBoxConfirm(
          `Esto desactivará ${stores.length} tienda(s) en Uber Eats. ¿Continuar?`,
          { title: "Desactivar tiendas", okVariant: "danger", okTitle: "Desactivar", cancelTitle: "Cancelar", centered: true }
        )
        .then((ok) => {
          if (!ok) return;
          this.loading = true;
          axios
            .post("/uber/onboarding/deprovisioning", { stores })
            .then((r) => {
              const results = (r.data && r.data.results) || [];
              const okCount = results.filter((x) => x.status === "REQUESTED").length;
              const fail = results.length - okCount;
              this.makeToast(
                "Resultado",
                `OK: ${okCount}, Errores: ${fail}.`,
                fail === 0 ? "success" : "warning"
              );
              this.fetchEvents();
            })
            .catch((err) => this.handleErr(err))
            .finally(() => (this.loading = false));
        });
    },
    fetchEvents() {
      axios
        .get("/uber/onboarding/events", { params: { limit: 50 } })
        .then((r) => {
          this.events = r.data || [];
        })
        .catch((err) => this.handleErr(err));
    },
    handleErr(err) {
      const msg = err.request && err.request.response ? err.request.response : err.message;
      this.makeToast("Error", msg, "danger");
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    },
  },
};
</script>
